import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import api from '../../services/api';

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: #FFFDF7;
  padding: 40px 20px 60px;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const BackBtn = styled.button`
  background: none;
  border: 1px solid rgba(218, 165, 32, 0.25);
  color: #8B6914;
  padding: 8px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  margin-bottom: 24px;
  &:hover {
    border-color: #DAA520;
    color: #DAA520;
    background: rgba(218, 165, 32, 0.05);
  }
`;

const Title = styled.h1`
  text-align: center;
  font-family: 'Playfair Display', Georgia, serif;
  color: #2C1810;
  font-size: 2.2rem;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  text-align: center;
  color: #9B8B7A;
  font-size: 1rem;
  margin-bottom: 40px;
`;

const GoldDivider = styled.div`
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #DAA520, transparent);
  margin: 0.5rem auto 1.5rem;
`;

const RasiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.2rem;
  animation: ${slideUp} 1s ease-out 0.3s both;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const RasiCard = styled.div`
  background: #FFFFFF;
  border: 1px solid ${p => p.$color}18;
  border-radius: 16px;
  padding: 1.2rem;
  transition: all 0.3s;

  &:hover {
    border-color: ${p => p.$color}44;
    box-shadow: 0 4px 20px ${p => p.$color}12;
    transform: translateY(-2px);
  }
`;

const RasiHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 1rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid rgba(218, 165, 32, 0.08);
`;

const RasiSymbol = styled.span`font-size: 2rem; line-height: 1;`;
const RasiInfo = styled.div`flex: 1;`;

const RasiName = styled.div`
  color: #2C1810;
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 700;
  font-size: 1.05rem;
`;

const RasiEnglish = styled.div`
  color: #9B8B7A;
  font-size: 0.75rem;
  margin-top: 1px;
`;

const NakshatraRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const NakshatraChip = styled.button`
  flex: 1;
  min-width: 80px;
  padding: 10px 8px;
  border-radius: 10px;
  border: 1px solid ${p => p.$color}22;
  background: ${p => p.$active ? `${p.$color}12` : '#FFFDF7'};
  color: ${p => p.$active ? '#2C1810' : '#6B5744'};
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s;
  text-align: center;
  line-height: 1.3;

  &:hover {
    background: ${p => p.$color}12;
    border-color: ${p => p.$color}55;
    color: #2C1810;
    transform: scale(1.03);
  }
  &:active { transform: scale(0.97); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(44, 24, 16, 0.5);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: ${fadeIn} 0.2s ease-out;
`;

const PalanModalCard = styled.div`
  background: #FFFFFF;
  border: 1px solid ${p => p.$color}22;
  border-radius: 20px;
  padding: 2rem;
  max-width: 520px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  animation: ${slideUp} 0.3s ease-out;
  box-shadow: 0 20px 60px rgba(44, 24, 16, 0.15), 0 0 0 1px ${p => p.$color}11;
  @media (max-width: 600px) { padding: 1.5rem 1rem; }
`;

const ModalClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(218, 165, 32, 0.1);
  border: none;
  color: #8B6914;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s;
  &:hover { background: rgba(218, 165, 32, 0.2); }
`;

const ModalIcon = styled.div`font-size: 3.5rem; text-align: center; margin-bottom: 0.5rem;`;

const ModalTitle = styled.h3`
  color: #2C1810;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.3rem;
  font-weight: 700;
  text-align: center;
  margin: 0 0 0.25rem;
`;

const ModalSubtitle = styled.p`
  color: #9B8B7A;
  font-size: 0.8rem;
  text-align: center;
  margin: 0 0 1.5rem;
`;

const LoadingText = styled.p`color: #9B8B7A; text-align: center; font-size: 0.9rem;`;

const PalanContent = styled.p`
  color: #6B5744;
  font-size: 0.95rem;
  line-height: 1.8;
  margin: 0;
  text-align: center;
  @media (max-width: 600px) { font-size: 0.9rem; line-height: 1.7; }
`;

const RASI_DATA = [
  { sign: 'aries', name: 'Mesha', english: 'Aries', symbol: '\u2648', color: '#e74c3c',
    nakshatras: [{ name: 'Ashwini', symbol: '\uD83D\uDC34' }, { name: 'Bharani', symbol: '\uD83D\uDD31' }, { name: 'Krittika', symbol: '\uD83D\uDD25' }] },
  { sign: 'taurus', name: 'Vrishabha', english: 'Taurus', symbol: '\u2649', color: '#27ae60',
    nakshatras: [{ name: 'Krittika', symbol: '\uD83D\uDD25' }, { name: 'Rohini', symbol: '\uD83D\uDC02' }, { name: 'Mrigashira', symbol: '\uD83E\uDD8C' }] },
  { sign: 'gemini', name: 'Mithuna', english: 'Gemini', symbol: '\u264A', color: '#f39c12',
    nakshatras: [{ name: 'Mrigashira', symbol: '\uD83E\uDD8C' }, { name: 'Ardra', symbol: '\uD83D\uDCA7' }, { name: 'Punarvasu', symbol: '\uD83C\uDFF9' }] },
  { sign: 'cancer', name: 'Karka', english: 'Cancer', symbol: '\u264B', color: '#3498db',
    nakshatras: [{ name: 'Punarvasu', symbol: '\uD83C\uDFF9' }, { name: 'Pushya', symbol: '\uD83C\uDF38' }, { name: 'Ashlesha', symbol: '\uD83D\uDC0D' }] },
  { sign: 'leo', name: 'Simha', english: 'Leo', symbol: '\u264C', color: '#e67e22',
    nakshatras: [{ name: 'Magha', symbol: '\uD83D\uDC51' }, { name: 'Purva Phalguni', symbol: '\uD83C\uDF05' }, { name: 'Uttara Phalguni', symbol: '\u2600\uFE0F' }] },
  { sign: 'virgo', name: 'Kanya', english: 'Virgo', symbol: '\u264D', color: '#8e44ad',
    nakshatras: [{ name: 'Uttara Phalguni', symbol: '\u2600\uFE0F' }, { name: 'Hasta', symbol: '\u270B' }, { name: 'Chitra', symbol: '\uD83D\uDC8E' }] },
  { sign: 'libra', name: 'Tula', english: 'Libra', symbol: '\u264E', color: '#1abc9c',
    nakshatras: [{ name: 'Chitra', symbol: '\uD83D\uDC8E' }, { name: 'Swati', symbol: '\uD83C\uDF31' }, { name: 'Vishakha', symbol: '\uD83C\uDFDB\uFE0F' }] },
  { sign: 'scorpio', name: 'Vrishchika', english: 'Scorpio', symbol: '\u264F', color: '#c0392b',
    nakshatras: [{ name: 'Vishakha', symbol: '\uD83C\uDFDB\uFE0F' }, { name: 'Anuradha', symbol: '\uD83E\uDEB7' }, { name: 'Jyeshtha', symbol: '\u2602\uFE0F' }] },
  { sign: 'sagittarius', name: 'Dhanu', english: 'Sagittarius', symbol: '\u2650', color: '#9b59b6',
    nakshatras: [{ name: 'Moola', symbol: '\uD83C\uDF3F' }, { name: 'Purva Ashadha', symbol: '\uD83C\uDF0A' }, { name: 'Uttara Ashadha', symbol: '\uD83D\uDC18' }] },
  { sign: 'capricorn', name: 'Makara', english: 'Capricorn', symbol: '\u2651', color: '#2c3e50',
    nakshatras: [{ name: 'Uttara Ashadha', symbol: '\uD83D\uDC18' }, { name: 'Shravana', symbol: '\uD83D\uDC42' }, { name: 'Dhanishta', symbol: '\uD83E\uDD41' }] },
  { sign: 'aquarius', name: 'Kumbha', english: 'Aquarius', symbol: '\u2652', color: '#2980b9',
    nakshatras: [{ name: 'Dhanishta', symbol: '\uD83E\uDD41' }, { name: 'Shatabhisha', symbol: '\u2B55' }, { name: 'Purva Bhadrapada', symbol: '\u2694\uFE0F' }] },
  { sign: 'pisces', name: 'Meena', english: 'Pisces', symbol: '\u2653', color: '#16a085',
    nakshatras: [{ name: 'Purva Bhadrapada', symbol: '\u2694\uFE0F' }, { name: 'Uttara Bhadrapada', symbol: '\uD83D\uDC0D' }, { name: 'Revati', symbol: '\uD83D\uDC1F' }] },
];

const DailyHoroscope = () => {
  const navigate = useNavigate();
  const [palanModal, setPalanModal] = useState(null);

  const handleNakshatraClick = async (nakshatra, rasi) => {
    const slug = nakshatra.toLowerCase();
    setPalanModal({ nakshatra, rasi, content: null, loading: true });
    try {
      const res = await api.get(`/nakshatras/${slug}/palan`);
      const content = res.data?.palan?.content || 'Daily palan is being prepared by our astrologers.';
      setPalanModal({ nakshatra, rasi, content, loading: false });
    } catch {
      setPalanModal({ nakshatra, rasi, content: 'Unable to load palan right now. Please try again later.', loading: false });
    }
  };

  return (
    <Page>
      <Container>
        <BackBtn onClick={() => navigate('/')}>Back to Home</BackBtn>
        <Title>Daily Nakshatra Prediction</Title>
        <GoldDivider />
        <Subtitle>Select your nakshatra to read today's prediction</Subtitle>

        <RasiGrid>
          {RASI_DATA.map((rasi) => (
            <RasiCard key={rasi.sign} $color={rasi.color}>
              <RasiHeader>
                <RasiSymbol>{rasi.symbol}</RasiSymbol>
                <RasiInfo>
                  <RasiName>{rasi.name}</RasiName>
                  <RasiEnglish>{rasi.english}</RasiEnglish>
                </RasiInfo>
              </RasiHeader>
              <NakshatraRow>
                {rasi.nakshatras.map((n) => (
                  <NakshatraChip
                    key={n.name}
                    $color={rasi.color}
                    $active={palanModal?.nakshatra === n.name && palanModal?.rasi?.sign === rasi.sign}
                    onClick={() => handleNakshatraClick(n.name, rasi)}
                  >
                    {n.symbol} {n.name}
                  </NakshatraChip>
                ))}
              </NakshatraRow>
            </RasiCard>
          ))}
        </RasiGrid>
      </Container>

      {palanModal && (
        <ModalOverlay onClick={() => setPalanModal(null)}>
          <PalanModalCard $color={palanModal.rasi.color} onClick={e => e.stopPropagation()}>
            <ModalClose onClick={() => setPalanModal(null)}>&#x2715;</ModalClose>
            <ModalIcon>{palanModal.rasi.symbol}</ModalIcon>
            <ModalTitle>{palanModal.nakshatra}</ModalTitle>
            <ModalSubtitle>{palanModal.rasi.name} ({palanModal.rasi.english}) — Today's Horoscope</ModalSubtitle>
            {palanModal.loading ? (
              <LoadingText>Loading your palan...</LoadingText>
            ) : (
              <PalanContent>{palanModal.content}</PalanContent>
            )}
          </PalanModalCard>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default DailyHoroscope;
